<template>
  <form @submit.prevent>
    <div>
      <h2>
        <span v-if="isEditMode">{{ $t("policy.name") }} "{{ editedEntity.name | translate }}" {{ $t("common.update") }}</span>
        <span v-else>{{ $t("policy.new") }}</span>
      </h2>

      <div class="row">
        <div class="col-md-4">
          <h6 class="mt-3">{{ $t("policy-category.text") }}</h6>
          <select class="form-control mb-3" v-model="form.category">
            <option value="ADMINISTRATIVE">{{ $t("policy-category.administrative") }}</option>
            <option value="TECHNICAL">{{ $t("policy-category.technical") }}</option>
            <option value="LEGAL">{{ $t("policy-category.legal") }}</option>
          </select>

          <b-form-checkbox switch v-model="form.isPeriodical" :value="true" :unchecked-value="false" class="custom-checkbox mb-3 mt-3">
            {{ $t("policy.is-periodical") }}
          </b-form-checkbox>
        </div>
        <div class="col-md-8">
          <sweet-input
            class="mt-3"
            v-model="form.name"
            :isValid="!$v.form.name.$error"
            @input="$v.form.name.$touch()"
            layout="vertical"
            :placeholder="$t('policy.name')"
            :label="$t('policy.name')"
          />
          <sweet-input
            v-if="form.isPeriodical"
            :isValid="isPeriodValid"
            v-model="form.period"
            type="number"
            :min="1"
            layout="vertical"
            :placeholder="$t('policy.period')"
            :label="$t('policy.period')"
          />
        </div>
      </div>

      <hr />

      <div class="modal-question-card p-2" style="background-color: white !important;">
        <div class="row">
          <div class="col-md-2">
            <label>Uyumluluk</label>
          </div>
          <div class="col-md-2">
            <label> Tedbir Grubu</label>
          </div>
          <div class="col-md-2">
            <label>Tedbir No</label>
          </div>
          <div class="col-md-2">
            <label>Tedbir Seviyesi</label>
          </div>
          <div class="col-md-4 mt-1">
            <label>Tedbir Tanımı</label>
          </div>
        </div>

        <table class="table table-bordered table-striped bg-white">
          <thead class="bg-secondary text-white">
            <tr>
              <td class="w-15">Uyumluluk</td>
              <td class="w-25">Tedbir Grubu</td>
              <td class="w-10">Tedbir No</td>
              <td class="w-10">Tedbir Seviyesi</td>
              <td class="w-25">Tedbir Tanımı</td>
              <td class="w-5"></td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(compliance, index) in form.compliances" :key="compliance._id">
              <td>{{ compliance.product }}</td>
              <td>{{ compliance.policyGroup.name | translate }}</td>
              <td>{{ compliance.policyNo }}</td>
              <td>{{ compliance.policyLevel }}</td>
              <td>{{ compliance.policyDefinition | translate }}</td>
              <td><i @click="removeCompliance(index)" class="bx bx-trash-alt text-danger hand" v-b-tooltip.hover :title="$t('common.delete')"></i></td>
            </tr>
            <tr>
              <td>
                <select class="form-control " v-model="compliance.product">
                  <option :value="undefined">{{ $t("common.clear-selection") }}</option>
                  <option v-for="item in scopedProducts" :key="item" :value="item">{{ item }}</option>
                </select>
              </td>
              <td>
                <filter-select
                  key="policyGroupFilter"
                  id="policyGroupFilter"
                  ref="policyGroupFilter"
                  placeholder="Tedbir Grubu"
                  :selected="compliance.policyGroup"
                  @filter="onPolicyGroupSearch"
                  @changed="onPolicyGroupSelect"
                />
              </td>
              <td><sweet-input v-model="compliance.policyNo" :isValid="true" /></td>
              <td>
                <select class="form-control mb-3" v-model="compliance.policyLevel">
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                </select>
              </td>
              <td><sweet-textarea v-model="compliance.policyDefinition" :isValid="true" :rows="5" /></td>
              <td>
                <b-button :disabled="this.$v.compliance.$error" class="mr-2" variant="main" @click="addCompliance()">kaydet</b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="modal-question-card p-2 mt-4" style="background-color: white !important;">
        <div style="width: 50%;">
          <h6 class="mt-4">{{ $t("risk.plural") }}</h6>

          <filter-select
            id="riskFilter"
            ref="riskFilter"
            :placeholder="$t('risk.text')"
            :multiple="true"
            :selected="form.riskDefinitions"
            @filter="onRiskSearch"
            @changed="onRiskSelect"
          />
        </div>
      </div>

      <div class="d-flex mb-3" style="gap:1rem">
        <div class="mt-3" style="width: 50%;">
          <h6 class="mt-2">{{ $t("task.plural") }}</h6>
          <div v-for="task in form.taskDefinitions" :key="task._id" class="card card-body">
            <sweet-button :only-icon="true" variant="false" icon="mdi mdi-delete" buttonClass="text-danger hidden float-right" @click="removeTaskItem(task)" />
            <sweet-button :only-icon="true" variant="false" icon="mdi mdi-pencil" buttonClass="text-warning hidden float-right" @click="editTaskItem(task)" />

            <h6>{{ $t("task.name") }}</h6>
            {{ task.name | translate }}
            <h6 class="mt-2">{{ $t("task.description") }}</h6>
            {{ task.description | translate }}

            <h6 class="mt-2">{{ $t("role.text") }}</h6>
            <div v-if="task.role && task.directAssign">
              <span class="badge badge-soft-primary">
                {{ task.role.name | translate }}
              </span>
            </div>
            <h6>Atama tercihi</h6>
            <span v-if="task.assignToCausedAccount">{{ $t("system-flow.assign.caused-account") }} </span>
            <span v-if="task.assignToOrganizationOwner">Organizasyon Sahibi </span>
            <span v-if="task.directAssign">Seçili Rol </span>

            <div v-if="task.blockedByTasks && task.blockedByTasks.length > 0">
              <h6>{{ $t("task.blocked-by") }}</h6>
              <ol>
                <li v-for="t in task.blockedByTasks" :key="t._id">{{ t.name | translate }}</li>
              </ol>
            </div>
          </div>

          <policy-task-definition-form store="managementPolicy" />
        </div>
      </div>

      <hr />

      <div class="modal-question-card p-2 m-4" style="background-color: white !important;" v-for="condition in form.conditions" :key="condition._id">
        <div class="d-flex align-items-center">
          <div class="pcard--actions">
            <i class="iconoir-edit" @click="editCondition(condition)"></i>
            <i class="iconoir-trash" @click="removeCondition(condition._id)"></i>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <select class="form-control mb-3" disabled v-model="condition.type">
              <option value="ON_PRODUCT_ENABLED">Ürün Aktif Olduğunda</option>
              <option value="ON_ASSET_GROUP_CIRITICALITY_CHANGED">Varlık Grubu Kritikliği Değiştiğinde</option>
              <option value="ON_RISK_ASSET_GROUP_RELATION_CREATED">Risk-Varlık Grubu İlişkisi Kurulduğunda</option>
            </select>
          </div>

          <div class="col-md-6">
            <b-form-tags v-model="condition.params.products" size="lg" class="mb-2" add-on-change no-outer-focus>
              <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
                <b-form-select v-bind="inputAttrs" disabled v-on="inputHandlers" :options="filteredProducts">
                  <template #first>
                    <option disabled value=""> {{ $t("product.text") }}</option>
                  </template>
                </b-form-select>

                <ul v-if="tags.length > 0" class="list-inline d-inline-block mt-2">
                  <li v-for="tag in tags" :key="tag" class="list-inline-item">
                    <b-form-tag @remove="removeTag(tag)" :title="tag" :disabled="disabled" variant="primary">{{ tag }}</b-form-tag>
                  </li>
                </ul>
                <b-form-text v-else>
                  <span class="text-danger">{{ $t("product.required") }}</span>
                </b-form-text>
              </template>
            </b-form-tags>

            <div v-if="condition && condition.type === 'ON_ASSET_GROUP_CIRITICALITY_CHANGED'">
              <h6 class="mt-4">{{ $t("asset-group-category.text") }}</h6>

              <p v-if="condition.params.assetGroupCategory">{{ $options.filters.translate(condition.params.assetGroupCategory.name) }}</p>
              <p v-else>-</p>
            </div>
          </div>
        </div>
      </div>

      <h6 v-if="!addNewConditionPanel" @click="addNewConditionPanel = true" class="hand text-primary">
        <i class="bx bx-plus"></i>
        koşul ekle
      </h6>
      <hr />
      <div class="modal-question-card p-2" style="background-color: white !important;" v-if="addNewConditionPanel">
        <div class="row">
          <div class="col-md-6">
            <select class="form-control mb-3" v-model="condition.type">
              <option value="ON_PRODUCT_ENABLED">Ürün Aktif Olduğunda</option>
              <option value="ON_ASSET_GROUP_CIRITICALITY_CHANGED">Varlık Grubu Kritikliği Değiştiğinde</option>
              <option value="ON_RISK_ASSET_GROUP_RELATION_CREATED">Risk-Varlık Grubu İlişkisi Kurulduğunda</option>
            </select>

            <div v-if="condition && condition.type === 'ON_ASSET_GROUP_CIRITICALITY_CHANGED'">
              <h6 class="mt-4">{{ $t("asset-group-category.text") }}</h6>
              <filter-select
                id="assetGroupCategoryFilter"
                ref="assetGroupCategoryFilter"
                :placeholder="$t('asset-group-category.text')"
                :multiple="false"
                @filter="onAssetGroupCategorySearch"
                :selected="condition.params.assetGroupCategory"
                @changed="onAssetGroupCategorySelect"
              />
            </div>
          </div>
          <div class="col-md-6">
            <b-form-tags v-model="condition.params.products" size="lg" class="mb-2" add-on-change no-outer-focus>
              <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
                <b-form-select v-bind="inputAttrs" v-on="inputHandlers" :options="filteredProducts">
                  <template #first>
                    <option disabled value=""> {{ $t("product.text") }}</option>
                  </template>
                </b-form-select>

                <ul v-if="tags.length > 0" class="list-inline d-inline-block mt-2">
                  <li v-for="tag in tags" :key="tag" class="list-inline-item">
                    <b-form-tag @remove="removeTag(tag)" :title="tag" :disabled="disabled" variant="primary">{{ tag }}</b-form-tag>
                  </li>
                </ul>
                <b-form-text v-else>
                  <span class="text-danger">{{ $t("product.required") }}</span>
                </b-form-text>
              </template>
            </b-form-tags>
          </div>
        </div>

        <div>
          <div class="d-flex flex-wrap  justify-content-between">
            <b-button :disabled="$v.condition.$invalid" variant="main" @click="addCondition()">Koşulu Kaydet</b-button>
            <b-button variant="secondary" @click="clearConditionForm()">Vazgeç</b-button>
          </div>
        </div>
      </div>
      <hr />
      <div v-if="form.conditions && form.conditions.some((i) => i.type == 'ON_ASSET_GROUP_CIRITICALITY_CHANGED' || i.type == 'ON_RISK_ASSET_GROUP_RELATION_CREATED')">
        <h4>Varlık Grubu Bazlı</h4>
        <b-form-checkbox switch v-model="form.assetGroupBased" />
      </div>

      <sweet-button
        @click="save()"
        type="submit"
        :loading="isUpdating"
        :disabled="isUpdating || $v.form.$invalid || !isPeriodValid"
        variant="success"
        icon="mdi mdi-check"
        size="sm"
        buttonClass="mt-2"
      >
        {{ $t("common.save") }}
      </sweet-button>
    </div>
  </form>
</template>

<script>
import SweetTextarea from "@c/form/sweet-textarea";
import { maxLength, minLength, required, requiredIf } from "vuelidate/lib/validators";
import RiskForm from "../risk/management-library-risk-definition-form";
import PolicyTaskDefinitionForm from "./component/policy-task-definition-form";

const state = "managementPolicy";

export default {
  components: {
    SweetTextarea,
    PolicyTaskDefinitionForm,
    RiskForm,
  },
  data() {
    return {
      form: {
        name: undefined,
        category: "ADMINISTRATIVE",
        conditions: [],
        compliances: [],
        period: 0,
        riskDefinitions: [],
        taskDefinitions: [],
      },
      compliance: {
        product: undefined,
        policyGroup: undefined,
        policyNo: undefined,
        policyLevel: 1,
        policyDefinition: undefined,
      },
      condition: {
        _id: undefined,
        type: "ON_PRODUCT_ENABLED",
        params: {
          products: [],
          assetGroupCategory: undefined,
        },
      },

      addNewConditionPanel: false,
      formError: undefined,
    };
  },
  validations: {
    form: {
      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(350),
      },

      category: {
        required,
      },
      riskDefinitions: {
        required: requiredIf(function(form) {
          return form.conditions?.some((i) => i.type == "ON_RISK_ASSET_GROUP_RELATION_CREATED");
        }),
      },
      compliances: {
        required,
      },
      conditions: {
        required,
      },
    },
    compliance: {
      product: {
        required,
      },
      policyGroup: {
        required,
      },
      policyLevel: {
        required,
      },
      policyNo: {
        required,
      },
      policyDefinition: {
        required,
      },
    },

    condition: {
      type: {
        required,
      },
      params: {
        products: {
          required,
        },
        assetGroupCategory: {
          required: requiredIf(function() {
            return this?.condition?.type == "ON_ASSET_GROUP_CIRITICALITY_CHANGED";
          }),
        },
      },
    },
  },

  watch: {
    condition: {
      deep: true,
      immediate: true,
      handler(n, o) {
        this.$v.condition.$touch();
      },
    },
    "form.compliances": {
      deep: true,
      immediate: true,
      handler(v, o) {
        //&& o && o?.length > 0
        if (!v || v?.length == 0) {
          this.$refs?.["riskFilter"]?.reset();
          // if (this.editedEntity) {
          //   this.form.riskDefinitions = this.editedEntity.riskDefinitions;
          // }
        }
      },
    },
    compliance: {
      deep: true,
      immediate: true,
      handler(n, o) {
        this.$v.compliance.$touch();
      },
    },
    "compliance.product": {
      deep: true,
      immediate: true,
      handler(n, o) {
        this.$refs?.["policyGroupFilter"]?.reset();
      },
    },
    editedEntity: {
      deep: true,
      immediate: true,
      handler(v) {
        this.$store.commit(`${state}/tasks`, v.taskDefinitions);

        // this.form = { ...this.prepareFormForEdit(v) };
        this.form = JSON.parse(JSON.stringify(this.prepareFormForEdit(v)));
      },
    },
    tasks: {
      deep: true,
      handler(v) {
        if (v) {
          this.form.taskDefinitions = [...v];
        } else {
          this.form.taskDefinitions = [];
        }
        this.$forceUpdate();
      },
    },
  },
  computed: {
    filteredProducts() {
      return this.form.compliances?.length ? this.form?.compliances.map((i) => i.product) : [];
    },
    tasks() {
      return this.$store.getters[`${state}/tasks`];
    },
    isUpdating() {
      return this.$store.getters[`${state}/isUpdating`];
    },
    editedEntity() {
      return this.$store.getters[`${state}/editedEntity`];
    },
    isEditMode() {
      return this.$store.getters[`${state}/isEditMode`];
    },
    isPeriodValid() {
      if (this.form.period < 1 && this.form.isPeriodical) return false;
      return true;
    },
  },
  mounted() {},
  methods: {
    async onAssetGroupCategorySearch(value, callback) {
      const response = await this.$store.dispatch(`managementAssetGroupCategory/paginate`, {
        options: { limit: 200 },
        query: { name: { value }, "scope.value": { value: "system", isObject: true } },
      });

      callback(response.data?.docs ?? []);
    },
    onAssetGroupCategorySelect(assetGroupCategory) {
      this.condition.params = { ...this.condition.params, assetGroupCategory };
    },
    resetCompliance() {
      this.compliance.product = undefined;
      this.compliance.policyGroup = undefined;
      this.compliance.policyNo = undefined;
      this.compliance.policyDefinition = undefined;
      this.$forceUpdate();
    },

    removeCondition(id) {
      this.form.conditions = [...this.form.conditions.filter((i) => i._id != id)];
      this.$v.form.conditions.$touch();
      this.$forceUpdate();
    },

    addCompliance() {
      if (!this.form.compliances) {
        this.$set(this.form, "compliances", []);
      }
      this.$set(this.form.compliances, this.form.compliances.length, {
        ...this.compliance,
        policyDefinition: {
          [this.currentLanguage]: this.compliance.policyDefinition,
        },
      });
      this.resetCompliance();
      this.$refs["riskFilter"]?.searchChanged("*");
      this.$forceUpdate();
    },
    includesAny(array, items) {
      return items.some((item) => array.includes(item));
    },
    removeCompliance(index) {
      this.form.compliances.splice(index, 1);
      this.form.riskDefinitions = this.form.riskDefinitions?.filter((i) => i.products.some((product) => this.filteredProducts.includes(product)));
      this.$forceUpdate();
    },
    editCondition(condition) {
      this.addNewConditionPanel = true;

      this.condition = { ...condition };
      this.$v.condition.$touch();
      this.$v.form.conditions.$touch();
      this.$forceUpdate();
    },
    clearConditionForm() {
      this.addNewConditionPanel = false;

      this.condition = {
        _id: undefined,
        type: "ON_PRODUCT_ENABLED",
        params: {
          products: [],
          assetGroupCategory: undefined,
        },
      };
    },
    addCondition() {
      if (!this.condition._id) {
        this.condition._id = "id" + new Date().getTime();
      }

      if (!this.form.conditions) {
        this.$set(this.form, "conditions", []);
      }

      // Mevcut koşulu diziden filtreleyip, yeni koşulu ekliyoruz
      const updatedConditions = [...this.form.conditions.filter((i) => i._id != this.condition._id), this.condition];

      this.$set(this.form, "conditions", updatedConditions);

      this.clearConditionForm();
      this.$v.condition.$touch();
      this.$v.form.conditions.$touch();
      this.$forceUpdate();
    },

    async save() {
      const ok = await this.confirm(this.$t(`alert.approve`));
      if (!ok) {
        return;
      }
      if (this.form.conditions.some((i) => i.type == "ON_RISK_ASSET_GROUP_RELATION_CREATED")) {
        if (!this.form.riskDefinitions || this.form.riskDefinitions?.length == 0) {
          return;
        }
      }

      try {
        await this.$store.dispatch(`${state}/save`, this.form);

        this.form = {};
        this.$toast.success(this.$t("common.success"));
        this.$root.$emit("bv::toggle::collapse", `sidebar-new-policy`);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    async onPolicyGroupSearch(value, callback) {
      if (!this.compliance.product) {
        return callback([]);
      }
      const response = await this.$store.dispatch(`managementPolicyGroup/paginate`, {
        options: { limit: 200, sort: { [`name.${this.currentLanguage || tr}`]: "asc" } },
        query: {
          "scope.value": { value: "system", isObject: true },
          name: { value },
          products: { value: this.compliance.product, isObject: true },
        },
      });

      callback(response.data?.docs ?? []);
    },
    onPolicyGroupSelect(value) {
      this.compliance.policyGroup = value;
    },

    async onRiskSearch(value, callback) {
      if (this.form.compliances && this.form.compliances.length > 0) {
        const response = await this.$store.dispatch(`managementRiskDefinition/paginate`, {
          options: { limit: 200 },
          query: {
            "scope.value": { value: "system", isObject: true },
            $or: { value: this.filteredProducts.map((i) => ({ products: i })), isObject: true },
            name: { value },
          },
        });

        callback(response.data?.docs ?? []);
      }
    },
    onRiskSelect(riskDefinitions) {
      this.form.riskDefinitions = riskDefinitions;
    },

    removeTaskItem(task) {
      this.$store.commit(`${state}/removeTaskItem`, { task });
    },
    editTaskItem(task) {
      this.$store.commit(`${state}/editedTask`, { ...task });

      this.$root.$emit("bv::toggle::collapse", `sidebar-create-task`);
    },
    rightPanelClosed() {
      if (this.$refs.riskFilter) {
        this.$refs["riskFilter"]?.reset();
      }
    },
    closeTaskPanel() {
      this.$refs["policy-task-defition-form"]?.closeTaskPanel();
    },
  },
};
</script>
